import type { FileNode } from "../types";

/**
 * Get the parent folder path for a given file/folder path.
 * Returns null if the item is at root level.
 */
export function getParentPath(itemPath: string): string | null {
  const lastSlash = itemPath.lastIndexOf("/");
  return lastSlash >= 0 ? itemPath.slice(0, lastSlash) : null;
}

/**
 * Recursively collect all file paths from a tree node.
 */
export function collectDescendantFiles(node: FileNode): string[] {
  if (node.type === "file") return [node.path];
  const files: string[] = [];
  for (const child of node.children ?? []) {
    files.push(...collectDescendantFiles(child));
  }
  return files;
}

/**
 * Check if a drop target is valid for a given dragged node.
 */
export function isValidDropTarget(
  draggedPath: string,
  draggedType: "file" | "folder",
  targetFolderPath: string | null,
): boolean {
  // Can't drop a folder on itself
  if (draggedType === "folder" && draggedPath === targetFolderPath)
    return false;

  // Can't drop a folder into its own descendant
  if (
    draggedType === "folder" &&
    targetFolderPath !== null &&
    targetFolderPath.startsWith(draggedPath + "/")
  ) {
    return false;
  }

  // Can't drop on current parent (would be a no-op)
  const currentParent = getParentPath(draggedPath);
  if (currentParent === targetFolderPath) return false;

  return true;
}

/**
 * Compute the list of file move operations needed to move a node to a new parent folder.
 * Returns an empty array if the move is invalid or would be a no-op.
 */
export function computeMoveOperations(
  draggedNode: FileNode,
  targetFolderPath: string | null,
): { fromPath: string; toPath: string }[] {
  if (
    !isValidDropTarget(draggedNode.path, draggedNode.type, targetFolderPath)
  ) {
    return [];
  }

  if (draggedNode.type === "file") {
    const newPath = targetFolderPath
      ? `${targetFolderPath}/${draggedNode.name}`
      : draggedNode.name;
    if (newPath === draggedNode.path) return [];
    return [{ fromPath: draggedNode.path, toPath: newPath }];
  }

  // For folders: move all descendant files, preserving subfolder structure
  const files = collectDescendantFiles(draggedNode);
  const newFolderBase = targetFolderPath
    ? `${targetFolderPath}/${draggedNode.name}`
    : draggedNode.name;

  return files
    .map((filePath) => {
      const relativePath = filePath.slice(draggedNode.path.length + 1);
      return {
        fromPath: filePath,
        toPath: `${newFolderBase}/${relativePath}`,
      };
    })
    .filter((m) => m.fromPath !== m.toPath);
}
